// ADR-017 Fase 4: SQLite local eliminado — leer de remote/cache.
import { getSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { buildCatalogSyncOperationId } from '../../sync/domain-api';
import { logBusinessAuditEvent } from '../../shell/audit/auditLogs';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { appendPromotionEvent, type PromotionEventType } from './promotionEventQueries';
import type {
  Promotion,
  PromotionChannel,
  PromotionConditions,
  PromotionTarget,
  PromotionTargetType,
  PromotionUpsertInput,
  PromotionWithTargets,
} from '../domain/promotionTypes';
import { DEFAULT_PROMOTION_CHANNELS } from '../domain/promotionTypes';

export type PromotionRow = {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  kind: string;
  scope: string;
  value_percent: number | null;
  value_fixed: number | null;
  combo_buy_qty: number | null;
  combo_pay_qty: number | null;
  combo_bundle_price: number | null;
  starts_at: string | null;
  ends_at: string | null;
  is_active: number | boolean | null;
  branch_ids: string | null;
  apply_mode: string;
  promo_code: string | null;
  priority: number | null;
  stackable: number | boolean | null;
  channels: string | null;
  conditions_json: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
};

export type PromotionTargetRow = {
  id: string;
  promotion_id: string;
  target_type: string;
  target_id: string;
  created_at: string | null;
};

export const parseBranchIds = (raw: string | null): string[] | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return parsed.map((v) => String(v));
  } catch {
    return null;
  }
};

export const serializeBranchIds = (branchIds: string[] | null | undefined): string | null => {
  if (!branchIds || branchIds.length === 0) return null;
  return JSON.stringify(branchIds);
};

export const parseChannels = (raw: string | null | undefined): PromotionChannel[] => {
  const allowed = new Set<PromotionChannel>(['pos', 'online']);
  const normalize = (arr: unknown[]): PromotionChannel[] => {
    const out = arr
      .map((v) => String(v).trim())
      .filter((v): v is PromotionChannel => allowed.has(v as PromotionChannel));
    return out.length > 0 ? [...new Set(out)] : [...DEFAULT_PROMOTION_CHANNELS];
  };
  if (!raw) return [...DEFAULT_PROMOTION_CHANNELS];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) return normalize(parsed);
  } catch {
    // ignore
  }
  return [...DEFAULT_PROMOTION_CHANNELS];
};

export const serializeChannels = (channels: PromotionChannel[] | null | undefined): string => {
  const normalized = parseChannels(channels ? JSON.stringify(channels) : null);
  return JSON.stringify(normalized);
};

export const parseConditions = (raw: string | null): PromotionConditions | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PromotionConditions;
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const serializeConditions = (conditions: PromotionConditions | null | undefined): string | null => {
  if (!conditions) return null;
  return JSON.stringify(conditions);
};

export const mapPromotionRow = (row: PromotionRow): Promotion => ({
  id: row.id,
  organizationId: row.organization_id,
  name: row.name,
  description: row.description,
  kind: row.kind as Promotion['kind'],
  scope: row.scope as Promotion['scope'],
  valuePercent: row.value_percent,
  valueFixed: row.value_fixed,
  comboBuyQty: row.combo_buy_qty,
  comboPayQty: row.combo_pay_qty,
  comboBundlePrice: row.combo_bundle_price,
  startsAt: row.starts_at,
  endsAt: row.ends_at,
  isActive: row.is_active === true || row.is_active === 1,
  branchIds: parseBranchIds(row.branch_ids),
  applyMode: row.apply_mode as Promotion['applyMode'],
  promoCode: row.promo_code,
  priority: row.priority ?? 0,
  stackable: row.stackable === true || row.stackable === 1,
  channels: parseChannels(row.channels),
  conditions: parseConditions(row.conditions_json),
  createdAt: row.created_at ?? new Date().toISOString(),
  updatedAt: row.updated_at ?? new Date().toISOString(),
  deletedAt: row.deleted_at,
});

export const mapTargetRow = (row: PromotionTargetRow): PromotionTarget => ({
  id: row.id,
  promotionId: row.promotion_id,
  targetType: row.target_type as PromotionTargetType,
  targetId: row.target_id,
  createdAt: row.created_at,
});

export const resolveOrganizationId = async () => {
  const organizationId = await getSessionOrganizationId();
  if (!organizationId) {
    throw new Error('No pudimos identificar el negocio.');
  }
  return organizationId;
};

type PromotionSyncPayload = Record<string, unknown>;

export const buildPromotionSyncPayload = (
  promotion: Promotion,
  targets: PromotionTarget[],
  organizationId: string,
  operation: 'insert' | 'update' | 'delete',
  expectedUpdatedAt?: string | null
): PromotionSyncPayload => {
  const updatedAt = promotion.updatedAt ?? new Date().toISOString();
  return {
    organizationId,
    id: promotion.id,
    operationId: buildCatalogSyncOperationId(promotion.id, { updatedAt }),
    // Optimistic lock (#1504): eco del updated_at visto en el último pull.
    // Sin eco (insert), el server cae al LWW legacy con tolerancia de skew.
    expectedUpdatedAt: expectedUpdatedAt ?? null,
    name: promotion.name,
    description: promotion.description ?? null,
    kind: promotion.kind,
    scope: promotion.scope,
    valuePercent: promotion.valuePercent ?? null,
    valueFixed: promotion.valueFixed ?? null,
    comboBuyQty: promotion.comboBuyQty ?? null,
    comboPayQty: promotion.comboPayQty ?? null,
    comboBundlePrice: promotion.comboBundlePrice ?? null,
    startsAt: promotion.startsAt ?? null,
    endsAt: promotion.endsAt ?? null,
    isActive: promotion.isActive,
    branchIds: promotion.branchIds ?? null,
    applyMode: promotion.applyMode,
    promoCode: promotion.promoCode ?? null,
    priority: promotion.priority,
    stackable: promotion.stackable,
    channels: promotion.channels?.length ? promotion.channels : [...DEFAULT_PROMOTION_CHANNELS],
    conditions: promotion.conditions ?? null,
    createdAt: promotion.createdAt,
    updatedAt,
    deletedAt: operation === 'delete' ? updatedAt : promotion.deletedAt ?? null,
    targets: targets.map((t) => ({
      id: t.id,
      targetType: t.targetType,
      targetId: t.targetId,
    })),
  };
};

export const buildPromotionFromInput = (
  organizationId: string,
  input: PromotionUpsertInput,
  existing?: Promotion
): Promotion => {
  const now = new Date().toISOString();
  return {
    id: existing?.id ?? crypto.randomUUID(),
    organizationId,
    name: input.name.trim(),
    description: input.description ?? null,
    kind: input.kind,
    scope: input.scope,
    valuePercent: input.valuePercent ?? null,
    valueFixed: input.valueFixed ?? null,
    comboBuyQty: input.comboBuyQty ?? null,
    comboPayQty: input.comboPayQty ?? null,
    comboBundlePrice: input.comboBundlePrice ?? null,
    startsAt: input.startsAt ?? null,
    endsAt: input.endsAt ?? null,
    isActive: input.isActive ?? existing?.isActive ?? true,
    branchIds: input.branchIds ?? null,
    applyMode: input.applyMode ?? existing?.applyMode ?? 'automatic',
    promoCode: input.promoCode ?? null,
    priority: input.priority ?? existing?.priority ?? 0,
    stackable: input.stackable ?? existing?.stackable ?? false,
    channels: input.channels?.length
      ? input.channels
      : existing?.channels?.length
        ? existing.channels
        : [...DEFAULT_PROMOTION_CHANNELS],
    conditions: input.conditions ?? existing?.conditions ?? null,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    // Al editar, limpiar deletedAt para restaurar la promo si estaba eliminada.
    deletedAt: null,
  };
};

export const buildTargetsFromInput = (
  promotionId: string,
  input: PromotionUpsertInput,
  existing: PromotionTarget[] = []
): PromotionTarget[] => {
  if (!input.targets) return existing;
  const now = new Date().toISOString();
  return input.targets
    .filter((t) => t.targetId.trim().length > 0)
    .map((t) => ({
      id: t.id ?? crypto.randomUUID(),
      promotionId,
      targetType: t.targetType,
      targetId: t.targetId.trim(),
      createdAt: now,
    }));
};

export const summarizePromotionForAudit = (promotion: Promotion, targets: PromotionTarget[]) => ({
  promotionId: promotion.id,
  name: promotion.name,
  kind: promotion.kind,
  scope: promotion.scope,
  isActive: promotion.isActive,
  targetCount: targets.length,
});

export const recordPromotionMutation = async (params: {
  eventType: PromotionEventType;
  auditEventType: string;
  promotion: Promotion;
  targets: PromotionTarget[];
  summary: string;
  payload?: Record<string, unknown>;
  before?: Record<string, unknown>;
}) => {
  const userId = useAuthStore.getState().session?.user?.id ?? null;
  const payload = {
    ...(params.payload ?? {}),
    ...(params.before ? { before: params.before } : {}),
    after: summarizePromotionForAudit(params.promotion, params.targets),
  };

  await appendPromotionEvent({
    promotionId: params.promotion.id,
    eventType: params.eventType,
    summary: params.summary,
    payload,
    performedByUserId: userId,
  });

  void logBusinessAuditEvent(params.auditEventType, {
    promotionId: params.promotion.id,
    name: params.promotion.name,
    kind: params.promotion.kind,
    scope: params.promotion.scope,
    ...(params.before ? { before: params.before } : {}),
    after: summarizePromotionForAudit(params.promotion, params.targets),
  });
};
